import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Grid,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { amber, blue, green, grey, red } from '@mui/material/colors';
import {
    getNextGroupId,
    getGroupableWithFirst,
    getEffectiveDoorStatus,
    isInactiveDoor,
    recalcLogical,
    rectIndices,
    rectIsValid,
    statusColorsForDoor,
    GROUP_BORDER,
    LOCKER_LEGEND_GROUPING,
} from './config/functions';
import { TutorialLockerSecondStepPanel } from './components/TutorialLockerSecondStepPanel';
import { useAppContext } from "../../../../../../contexts/AppContext";

export function SecondStepLockerForm({
                                         portas,
                                         setPortas,
                                         qtdColunas,
                                     }) {
    const { dadosUsuario } = useAppContext();

    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));
    const cols = Math.max(1, parseInt(qtdColunas) || 1);
    const sz = isSmall ? 36 : 48;
    const gap = 8;

    const [firstIdx, setFirstIdx] = useState(null);
    const [hoverIdx, setHoverIdx] = useState(null);

    const cancelSel = () => {
        setFirstIdx(null);
        setHoverIdx(null);
    };

    useEffect(() => {
        const handler = event => {
            if (event.key === 'Escape') {
                setFirstIdx(null);
                setHoverIdx(null);
            }
        };

        document.addEventListener('keydown', handler);

        return () => {
            document.removeEventListener('keydown', handler);
        };
    }, []);

    useEffect(() => {
        setFirstIdx(null);
        setHoverIdx(null);
    }, [portas.length, cols]);

    const previewSet = useMemo(() => {
        if (firstIdx === null || hoverIdx === null) {
            return new Set();
        }

        if (!rectIsValid(firstIdx, hoverIdx, portas, cols, portas.length)) {
            return new Set();
        }

        return new Set(
            rectIndices(firstIdx, hoverIdx, cols, portas.length)
        );
    }, [firstIdx, hoverIdx, portas, cols]);

    const groupableSet = useMemo(() => {
        if (firstIdx === null) return new Set();
        return getGroupableWithFirst(firstIdx, portas, cols, portas.length);
    }, [firstIdx, portas, cols]);

    const handleUndoGroup = groupId => {
        setPortas(currentPortas => {
            const updated = currentPortas.map((porta, index) => {
                const belongsToGroup =
                    porta.status === 'grouped' &&
                    String(porta.groupId) === String(groupId);

                if (!belongsToGroup) {
                    return porta;
                }

                return {
                    ...porta,
                    physIdx: index,
                    status: 'livre',
                    doorStatus: null,
                    groupId: null,
                    isPrimary: false,
                    numero_logico: null,
                    id_produto: null,
                    id_produto_pai: null,
                    qtd_produto: 0,
                    mustSelectChild: false,
                    is_dirty: true,
                };
            });

            return recalcLogical(updated);
        });

        cancelSel();
    };

    const handleCreateGroup = secondIdx => {
        if (firstIdx === null || secondIdx === firstIdx) {
            cancelSel();
            return;
        }

        if (!rectIsValid(firstIdx, secondIdx, portas, cols, portas.length)) {
            const novaPorta = portas[secondIdx];
            if (novaPorta && novaPorta.status === 'livre' && !isInactiveDoor(novaPorta)) {
                setFirstIdx(secondIdx);
                setHoverIdx(secondIdx);
            } else {
                cancelSel();
            }
            return;
        }

        const indexes = rectIndices(
            firstIdx,
            secondIdx,
            cols,
            portas.length
        );

        setPortas(currentPortas => {
            const groupId = getNextGroupId(currentPortas);
            const primaryIndex = Math.min(...indexes);

            const updated = currentPortas.map((porta, index) => {
                if (!indexes.includes(index)) {
                    return porta;
                }

                return {
                    ...porta,
                    physIdx: index,
                    status: 'grouped',
                    doorStatus: 'livre',
                    groupId,
                    isPrimary: index === primaryIndex,
                    numero_logico: null,
                    id_produto: null,
                    id_produto_pai: null,
                    qtd_produto: 0,
                    mustSelectChild: false,
                    is_dirty: true,
                };
            });

            return recalcLogical(updated);
        });

        cancelSel();
    };

    const handleClick = physIdx => {
        if (!dadosUsuario.isSempher) return;

        const porta = portas[physIdx];

        if (!porta || isInactiveDoor(porta)) {
            cancelSel();
            return;
        }

        if (porta.status === 'grouped') {
            if (firstIdx !== null) {
                cancelSel();
                return;
            }

            handleUndoGroup(porta.groupId);
            return;
        }

        if (porta.status !== 'livre') {
            cancelSel();
            return;
        }

        if (firstIdx === null) {
            setFirstIdx(physIdx);
            setHoverIdx(physIdx);
            return;
        }

        handleCreateGroup(physIdx);
    };

    function buildCells(currentPortas, totalCols) {
        const cells = [];
        const seenGroups = new Set();

        currentPortas.forEach((porta, physIdx) => {
            if (porta.status === 'grouped') {
                const groupKey = String(porta.groupId);

                if (seenGroups.has(groupKey)) {
                    return;
                }

                const memberIndexes = currentPortas
                    .map((member, index) => ({ member, index }))
                    .filter(({ member }) =>
                        member.status === 'grouped' &&
                        String(member.groupId) === groupKey
                    )
                    .map(({ index }) => index)
                    .sort((a, b) => a - b);

                if (memberIndexes.length === 0) {
                    return;
                }

                seenGroups.add(groupKey);

                const primaryIndex = memberIndexes[0];
                const primaryDoor = currentPortas[primaryIndex];
                const rows = memberIndexes.map(index =>
                    Math.floor(index / totalCols)
                );
                const columns = memberIndexes.map(index => index % totalCols);

                const minRow = Math.min(...rows);
                const maxRow = Math.max(...rows);
                const minCol = Math.min(...columns);
                const maxCol = Math.max(...columns);

                cells.push({
                    porta: primaryDoor,
                    physIdx: primaryIndex,
                    memberIndexes,
                    gridRow: minRow + 1,
                    gridCol: minCol + 1,
                    rowSpan: maxRow - minRow + 1,
                    colSpan: maxCol - minCol + 1,
                    grouped: true,
                });

                return;
            }

            const row = Math.floor(physIdx / totalCols);
            const col = physIdx % totalCols;

            cells.push({
                porta,
                physIdx,
                memberIndexes: [physIdx],
                gridRow: row + 1,
                gridCol: col + 1,
                rowSpan: 1,
                colSpan: 1,
                grouped: false,
            });
        });

        return cells;
    }

    const rows = portas.length > 0
        ? Math.ceil(portas.length / cols)
        : 1;

    const cells = buildCells(portas, cols);

    const isDisabledDuringSelection = (cell) => {
        if (firstIdx === null) return false;
        if (cell.physIdx === firstIdx) return false;
        // qualquer índice físico da célula que seja groupable habilita a célula
        return !cell.memberIndexes.some(idx => groupableSet.has(idx));
    };

    const getCellSx = (cell) => {
        const {
            porta,
            physIdx,
            gridRow,
            gridCol,
            rowSpan,
            colSpan,
            grouped,
        } = cell;

        const isFirst = physIdx === firstIdx;
        const isPreview = previewSet.has(physIdx) && !isFirst;
        const isBlocked = isInactiveDoor(porta);
        const disabledDuringSel = isDisabledDuringSelection(cell);
        const dimmed = disabledDuringSel && !isPreview;

        const width = colSpan * sz + (colSpan - 1) * gap;
        const height = rowSpan * sz + (rowSpan - 1) * gap;

        const base = {
            gridColumn: `${gridCol} / span ${colSpan}`,
            gridRow: `${gridRow} / span ${rowSpan}`,
            width: `${width}px`,
            height: `${height}px`,
            minWidth: 0,
            p: 0,
            fontSize: 12,
            fontWeight: 500,
            borderRadius: 1.5,
            transition:
                'background-color .15s, border-color .15s, opacity .15s, transform .15s',
            opacity: dimmed ? 0.3 : 1,
        };

        if (grouped) {
            const c = statusColorsForDoor(porta);
            return {
                ...base,
                backgroundColor: c.bg,
                border: GROUP_BORDER,
                color: c.color,
                '&:hover': { backgroundColor: c.hoverBg },
                '&.Mui-disabled': {
                    backgroundColor: c.bg,
                    border: GROUP_BORDER,
                    color: c.color,
                    opacity: dimmed ? 0.3 : 1,
                },
            };
        }

        if (isFirst) {
            return {
                ...base,
                backgroundColor: amber[300],
                border: `2px solid ${amber[600]}`,
                color: amber[900],
                transform: 'scale(1.05)',
                '&:hover': { backgroundColor: amber[400] },
            };
        }

        if (isPreview) {
            return {
                ...base,
                backgroundColor: blue[100],
                border: `1.5px solid ${blue[400]}`,
                color: blue[900],
                '&:hover': { backgroundColor: blue[200] },
            };
        }

        if (isBlocked) {
            return {
                ...base,
                backgroundColor: red[700],
                border: `1.5px solid ${red[900]}`,
                color: 'white',
                '&.Mui-disabled': {
                    backgroundColor: red[700],
                    border: `1.5px solid ${red[900]}`,
                    color: 'white',
                    opacity: dimmed ? 0.3 : 1,
                },
            };
        }

        const c = statusColorsForDoor(porta);
        return {
            ...base,
            backgroundColor: c.bg,
            border: `1.5px solid ${c.border}`,
            color: c.color,
            '&:hover': { backgroundColor: c.hoverBg },
            '&.Mui-disabled': {
                backgroundColor: c.bg,
                color: c.color,
                opacity: dimmed ? 0.3 : 1,
            },
        };
    };

    const getTooltipTitle = (cell) => {
        if (isInactiveDoor(cell.porta)) {
            return 'Porta inativada — reative-a antes de agrupar.';
        }
        if (cell.grouped) {
            return 'Clique para desfazer o agrupamento';
        }
        if (getEffectiveDoorStatus(cell.porta) === 'ocupado' || cell.porta.id_produto) {
            return 'Porta ocupada — remova o produto antes de agrupar.';
        }
        if (firstIdx !== null && isDisabledDuringSelection(cell)) {
            return 'Porta indisponível para formar retângulo com a selecionada.';
        }
        return '';
    };

    return (
        <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={3}
            sx={{ mt: 1 }}
        >
            <Grid item xs={12}>
                <Typography variant="h5" textAlign="center" gutterBottom>
                    {dadosUsuario.isSempher ? 'Deseja agrupar as portas?' : 'Mapeamento de portas'}
                </Typography>
            </Grid>

            <Grid item xs={12} md={7}>
                <Box
                    sx={{
                        border: `1px solid ${grey[300]}`,
                        borderRadius: 2,
                        p: 2,
                    }}
                    onMouseLeave={() => {
                        if (firstIdx !== null) {
                            setHoverIdx(firstIdx);
                        }
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${cols}, ${sz}px)`,
                            gridTemplateRows: `repeat(${rows}, ${sz}px)`,
                            gap: `${gap}px`,
                            justifyContent: 'center',
                        }}
                    >
                        {cells.map(cell => {
                            const inactive = isInactiveDoor(cell.porta);
                            const disabledDuringSel = isDisabledDuringSelection(cell);

                            return (
                                <Tooltip
                                    key={
                                        cell.grouped
                                            ? `group-${cell.porta.groupId}`
                                            : `door-${cell.physIdx}`
                                    }
                                    title={getTooltipTitle(cell)}
                                    placement="top"
                                    arrow
                                >
                                    <span style={{
                                        gridColumn: `${cell.gridCol} / span ${cell.colSpan}`,
                                        gridRow: `${cell.gridRow} / span ${cell.rowSpan}`,
                                    }}>
                                        <Button
                                            variant="contained"
                                            disableElevation
                                            fullWidth
                                            disabled={!dadosUsuario.isSempher || inactive || disabledDuringSel}
                                            onClick={() => handleClick(cell.physIdx)}
                                            onMouseEnter={() => {
                                                if (firstIdx !== null) {
                                                    setHoverIdx(cell.physIdx);
                                                }
                                            }}
                                            sx={getCellSx(cell)}
                                        >
                                            {(!cell.porta.numero_logico || isInactiveDoor(cell.porta)) ? '—' : cell.porta.numero_logico}
                                        </Button>
                                    </span>
                                </Tooltip>
                            );
                        })}
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        gap: 2,
                        mt: 1.5,
                        pl: 0.5,
                    }}
                >
                    {LOCKER_LEGEND_GROUPING.map(item => (
                        <Box
                            key={item.label}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 14,
                                    height: 14,
                                    borderRadius: '3px',
                                    backgroundColor: item.bg,
                                    border: item.dashed
                                        ? `2px dashed ${item.border}`
                                        : `1.5px solid ${item.border}`,
                                }}
                            />
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {item.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Grid>

            {dadosUsuario.isSempher && (
                <Grid item xs={12} md={4}>
                    <TutorialLockerSecondStepPanel />
                </Grid>
            )}
        </Grid>
    );
}